import { CSSProperties, useState } from 'react';

/**
 * ConfirmBar — fixed bottom confirmation banner: serif question + quiet
 * subline on the left, "change my mind" outline + inverted confirm on the
 * right. Fades/slides up on mount.
 */
export interface ConfirmBarProps {
  /** Serif headline, e.g. "Going with Matheus?". */
  title: string;
  /** Quiet subline. */
  subtitle?: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Disables both actions (e.g. while saving). */
  disabled?: boolean;
  style?: CSSProperties;
}

export function ConfirmBar({
  title, subtitle, confirmLabel, cancelLabel = 'Change my mind',
  onConfirm, onCancel, disabled = false, style,
}: ConfirmBarProps) {
  const [cancelHover, setCancelHover] = useState(false);
  const [confirmHover, setConfirmHover] = useState(false);

  return (
    <>
      <style>{`
        @keyframes sk-confirm-up{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        /* Equal-width, full-row buttons only below the point where they've
           already wrapped onto their own line under the copy — above that,
           they sit inline beside the text at their natural width instead. */
        .sk-confirmbar-actions{display:flex;gap:10px;flex:1 1 100%}
        .sk-confirmbar-actions button{flex:1;text-align:center}
        @media (min-width:520px){
          .sk-confirmbar-actions{flex:0 0 auto}
          .sk-confirmbar-actions button{flex:0 0 auto}
        }
      `}</style>
      <div
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'var(--surface)', borderTop: '1px solid var(--line)',
          padding: '16px clamp(20px, 5vw, 32px)',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
          display: 'flex', flexWrap: 'wrap', alignItems: 'center',
          justifyContent: 'space-between', gap: '12px 16px', zIndex: 50,
          fontFamily: 'var(--font-sans)',
          animation: 'sk-confirm-up .22s cubic-bezier(.2,.8,.3,1) both',
          ...style,
        }}
      >
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 17, color: 'var(--text)' }}>{title}</div>
          {subtitle ? <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 3 }}>{subtitle}</div> : null}
        </div>
        <div className="sk-confirmbar-actions">
          <button
            type="button"
            disabled={disabled}
            onClick={onCancel}
            onMouseEnter={() => setCancelHover(true)}
            onMouseLeave={() => setCancelHover(false)}
            style={{
              fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500,
              background: 'transparent', color: 'var(--text-soft)',
              border: `1px solid ${cancelHover ? 'var(--text-dim)' : 'var(--line)'}`,
              borderRadius: 999, padding: '10px 20px',
              cursor: disabled ? 'not-allowed' : 'pointer', transition: 'border-color .2s',
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={onConfirm}
            onMouseEnter={() => setConfirmHover(true)}
            onMouseLeave={() => setConfirmHover(false)}
            style={{
              fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
              background: 'var(--text)', color: 'var(--bg)',
              border: 'none', borderRadius: 999, padding: '10px 24px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: 'opacity .15s', opacity: disabled ? 0.6 : confirmHover ? 0.88 : 1,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}

export default ConfirmBar;
